import * as React from 'react';
import { MessageBarType } from '@fluentui/react';
import { useHistory } from 'react-router-dom';
import { Spinner, SpinnerSize } from '@fluentui/react';
import { TripleDES } from 'crypto-js';
import { Buffer } from 'buffer';
import { CmicContext } from '../../context/CmicContext';
import { CMIC_FIELD_REST__NAMES, CMIC_KEYS, ACTION_ROLE } from '../../shared/Constants';
import {
	getUsernameAndTenantId,
	getEnvironmentNameFromWSURL,
	handleLoginUserViaAuth2,
} from '../../shared/UtilityFunctions';
import axios from 'axios';
import { isBGClrDark, handleProjectDataFiller } from '../../utility/UtilityFunctions';
import { COLORS } from '../../utility/tasks/colors';
import { getAuthTokenForAuthCode } from '../../components/Auth/AuthHome';
import { getOfficeAccessToken } from '../../components/Auth/OAuth';
import { segmentIdentifyUser, segmentTrackLogin } from '../../utility/segment/segmentUtility';

// Check if JWT token is expired (with 5 minute buffer)
const isTokenExpired = (token) => {
	if (!token) return true;
	try {
		const payload = JSON.parse(atob(token.split('.')[1]));
		const expirationTime = payload.exp * 1000; // Convert to milliseconds
		const bufferTime = 5 * 60 * 1000; // 5 minutes buffer
		return Date.now() >= expirationTime - bufferTime;
	} catch (error) {
		console.log('Error checking token expiration:', error);
		return true; // Assume expired if we can't parse
	}
};

const DecidingLoader = () => {
	const { useEffect, useContext, useRef } = React;

	const { userInfo, setUserInfo, setShowToast } = useContext(CmicContext);

	const navigate = useHistory();

	// Guard to prevent handleSignIn from running more than once.
	// saveInfo() calls setUserInfo() which changes the userInfo context value,
	// re-triggering this useEffect. The ref ensures we only sign in once.
	const isSigningIn = useRef(false);

	useEffect(() => {
		if (!window.navigator.onLine) {
			navigate.replace('/settings');
			setShowToast({
				isVisible: true,
				msg: 'No internet',
				type: MessageBarType.error,
			});
		} else {
			if (!userInfo) return;
			if (isSigningIn.current) return;
			isSigningIn.current = true;
			handleSignIn(userInfo);
		}
	}, [userInfo]);

	const handleSignIn = async userInfo => {
		const params = new URLSearchParams(window.location.search);
		const authCode = params.get('code');
		if (authCode) {
			console.log(authCode);
			await localStorage.setItem('authCode', authCode);
			const data = await getAuthTokenForAuthCode();
			await localStorage.setItem('accessToken', data?.access_token);
			Office.context.ui.closeContainer();
			console.log(data);
		}
		if (!userInfo.password && !userInfo?.accessToken) {
			navigate.replace('/settings');
			return;
		}

		// Refresh token for OAuth users before making API calls
		let currentUserInfo = { ...userInfo };
		if (userInfo?.isCloudUser || userInfo?.isEnterpriseOAuth) {
			if (isTokenExpired(userInfo?.accessToken)) {
				try {
					const officeAccessTokenResponse = await getOfficeAccessToken();
					if (officeAccessTokenResponse.success) {
						currentUserInfo.accessToken = officeAccessTokenResponse.data;
					} else {
						// Token refresh failed, redirect to settings
						navigateToSettings({ message: 'Session expired. Please sign in again.' });
						return;
					}
				} catch (error) {
					navigateToSettings({ message: 'Session expired. Please sign in again.' });
					return;
				}
			}
		}

		let URL;
		if (currentUserInfo.actionRole === ACTION_ROLE.ECM) URL = `${currentUserInfo.serverURL}${CMIC_FIELD_REST__NAMES.LOGIN_ECM}`;
		else URL = `${currentUserInfo.serverURL}${CMIC_FIELD_REST__NAMES.LOGIN}`;

		try {
			const result = await axios.get(URL, {
				...handleLoginUserViaAuth2(currentUserInfo),
			});

			let projectListData;
			if (result.data) {
				if (result.data.defaultProjectUuid) {
					const projectData = await setDefaultProjectData(result.data, currentUserInfo);

					projectListData = handleProjectDataFiller(
						{
							value: projectData?.PmpProjOraseq,
							nodeId: projectData?.PmpProjOraseq,
							label: `${projectData?.PmpProjName} (${projectData?.PmpProjCode})`,
							timeStamp: new Date(),
							...projectData,
						},
						true,
					);
				}

				saveInfo(result.data, currentUserInfo.accessToken);
				if (currentUserInfo?.isCloudUser) {
					segmentIdentifyUser(result.data, {
						tenantID: currentUserInfo?.tenantID,
						isCloudUser: currentUserInfo?.isCloudUser,
					});
					segmentTrackLogin();
				}
				navigate.replace('/home');
				return;
			} else {
				setShowToast({
					isVisible: true,
					msg: 'Authentication Error',
					type: MessageBarType.error,
				});
				navigate.replace('/settings');
			}
		} catch (error) {
			console.log('handleSignIn Error=', error);
			navigateToSettings(error);
		}
	};

	const setDefaultProjectData = async (data, currentUserInfo) => {
		return new Promise(async (resolve, reject) => {
			const URL = `${currentUserInfo.serverURL}${CMIC_FIELD_REST__NAMES.projects}/${data.defaultProjectUuid}?onlyData=true`;
			try {
				const result = await axios.get(URL, {
					...handleLoginUserViaAuth2(currentUserInfo),
				});

				if (typeof result.data === 'object') {
					resolve(result.data);
				} else {
					navigateToSettings();
				}
			} catch (error) {
				navigateToSettings();
			}
		});
	};

	const saveInfo = (userData = null, refreshedToken = null) => {
		const requiredInfo = {
			appId: userInfo.appId,
			tenantID: userInfo.tenantID,
			username: userInfo.username,
			password: userInfo.password,
			serverURL: userInfo.serverURL,
			serverType: userInfo.serverType,
			isTreeShown: 0,
			actionRole: userInfo.actionRole,
			usernameAndTenantId: getUsernameAndTenantId(userInfo.tenantID, userInfo.username),
			environmentName: getEnvironmentNameFromWSURL(userInfo.serverURL),
			info: userData || {},
			accessToken: refreshedToken || userInfo?.accessToken,
			isCloudUser: userInfo?.isCloudUser,
			isEnterpriseOAuth: userInfo?.isEnterpriseOAuth,
		};

		let ciphertext = TripleDES.encrypt(
			JSON.stringify(requiredInfo),
			CMIC_KEYS.SERVER_SETTINGS_ENCRYPTION_KEY,
		).toString();

		const encodedString = Buffer.from(ciphertext).toString('base64');

		// setting userInfo after submitting data
		setUserInfo({
			...requiredInfo,
		});

		localStorage.setItem(CMIC_KEYS.SERVER_SETTINGS_ENCRYPTION_KEY, encodedString); // SAVING ENCRYPTED KEY
	};

	const navigateToSettings = error => {
		setShowToast({
			isVisible: true,
			msg: error?.message || 'Something went wrong!',
			type: MessageBarType.error,
		});
		navigate.replace('/settings');
	};

	return (
		<div style={{ height: '100%', width: '100%', display: 'block', paddingTop: '50%' }}>
			<Spinner
				color={isBGClrDark() ? COLORS.OUTLOOK_LIGHT_BLUE : COLORS.OUTLOOK_DARK_BLUE}
				size={SpinnerSize.large}
				label="Authenticating"
			/>
		</div>
	);
};

export default DecidingLoader;
